import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import {
  Shield, Package, Globe, Truck,
  ArrowRight, CheckCircle2,
  Menu, X, Phone, Mail
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_importacao-expert/artifacts/gz8vakfs_viamundi_logo_sem_fundo_cores_originais.png";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1591707677545-65b48b152ccd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxjYXJnbyUyMHNoaXAlMjBvY2VhbiUyMGRhcmslMjBjaW5lbWF0aWN8ZW58MHx8fHwxNzc2MTI2ODg3fDA&ixlib=rb-4.1.0&q=85",
  customs: "https://images.unsplash.com/photo-1638368885492-7df8ff32920d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBsb2dpc3RpY3MlMjBkYXJrfGVufDB8fHx8MTc3NjEyNjg4N3ww&ixlib=rb-4.1.0&q=85",
  importacao: "https://images.unsplash.com/photo-1694875522449-e852b63be76d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBsb2dpc3RpY3MlMjBkYXJrfGVufDB8fHx8MTc3NjEyNjg4N3ww&ixlib=rb-4.1.0&q=85",
  exportacao: "https://images.unsplash.com/photo-1738888674993-26420084fa72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMHNoaXAlMjBvY2VhbiUyMGRhcmslMjBjaW5lbWF0aWN8ZW58MHx8fHwxNzc2MTI2ODg3fDA&ixlib=rb-4.1.0&q=85",
  logistics: "https://images.unsplash.com/photo-1681584231607-3b73aeac51e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxjYXJnbyUyMHNoaXAlMjBvY2VhbiUyMGRhcmslMjBjaW5lbWF0aWN8ZW58MHx8fHwxNzc2MTI2ODg3fDA&ixlib=rb-4.1.0&q=85",
};

function FadeIn({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function LandingPage() {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [posts, setPosts] = useState([]);
  const [formData, setFormData] = useState({ nome: "", email: "", telefone: "", mensagem: "" });
  const [submitting, setSubmitting] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    axios.get(`${API}/blog`).then(r => setPosts(r.data)).catch(() => {});
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, formData);
      toast.success("Mensagem enviada! Entraremos em contato em breve.");
      setFormData({ nome: "", email: "", telefone: "", mensagem: "" });
    } catch {
      toast.error("Erro ao enviar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    { icon: Shield, title: "Despacho Aduaneiro", desc: "Processo ágil e sem erros para liberação de suas mercadorias com total conformidade legal.", img: IMAGES.customs, span: "md:col-span-7" },
    { icon: Package, title: "Importação", desc: "Redução de custos e riscos com planejamento tributário e operacional estratégico.", img: IMAGES.importacao, span: "md:col-span-5" },
    { icon: Globe, title: "Exportação", desc: "Operações seguras e eficientes para levar seus produtos ao mercado internacional.", img: IMAGES.exportacao, span: "md:col-span-5" },
    { icon: Truck, title: "Logística Internacional", desc: "Controle total da cadeia logística, do embarque à entrega no destino final.", img: IMAGES.logistics, span: "md:col-span-7" },
  ];

  const differentials = [
    "Redução real de custos tributários",
    "Mitigação de riscos operacionais",
    "Atendimento consultivo estratégico",
    "Atualização constante na legislação",
    "Equipe com experiência em Big 4",
    "Tecnologia e automação de processos",
  ];

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* NAVBAR */}
      <header
        data-testid="navbar"
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? "glass-nav" : "bg-transparent"}`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <a href="/" data-testid="logo" className="flex-shrink-0">
            <img src={LOGO_URL} alt="Viamundi Gestão Aduaneira" className="h-14 md:h-16 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#sobre" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Sobre</a>
            <a href="#servicos" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Serviços</a>
            <a href="#diferenciais" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Diferenciais</a>
            <a href="#blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Conteúdo</a>
            <a
              href="#contato"
              data-testid="nav-cta"
              className="text-sm font-bold uppercase tracking-widest px-6 py-3 bg-primary text-primary-foreground glow-cyan hover:scale-105 transition-transform"
            >
              Fale Conosco
            </a>
          </nav>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-foreground"
            onClick={() => setMobileMenu(!mobileMenu)}
          >
            {mobileMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {mobileMenu && (
          <div className="md:hidden glass-nav border-t border-border/20 px-6 py-4 space-y-3">
            <a href="#sobre" onClick={() => setMobileMenu(false)} className="block text-sm text-muted-foreground">Sobre</a>
            <a href="#servicos" onClick={() => setMobileMenu(false)} className="block text-sm text-muted-foreground">Serviços</a>
            <a href="#diferenciais" onClick={() => setMobileMenu(false)} className="block text-sm text-muted-foreground">Diferenciais</a>
            <a href="#blog" onClick={() => setMobileMenu(false)} className="block text-sm text-muted-foreground">Conteúdo</a>
            <a href="#contato" onClick={() => setMobileMenu(false)} className="block text-sm font-bold text-primary">Fale Conosco</a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20">
          <FadeIn>
            <p className="section-label mb-6">Assessoria em Comércio Exterior</p>
          </FadeIn>
          <FadeIn delay={100}>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] max-w-4xl">
              Reduza custos e elimine riscos no seu processo de importação
            </h1>
          </FadeIn>
          <FadeIn delay={200}>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Assessoria completa em comércio exterior com especialistas que cuidam de tudo para sua empresa operar com segurança e eficiência.
            </p>
          </FadeIn>
          <FadeIn delay={300}>
            <a href="#contato">
              <Button
                data-testid="hero-cta"
                className="mt-8 rounded-none px-8 py-6 font-bold uppercase tracking-widest text-sm bg-primary text-primary-foreground glow-cyan hover:scale-105 transition-transform"
              >
                Solicitar Consultoria <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </FadeIn>
{/* Stats removed - new company */}
        </div>
      </section>

      {/* SOBRE NOS */}
      <section id="sobre" data-testid="about-section" className="py-20 md:py-32 px-6 md:px-12 bg-muted/30">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <FadeIn>
            <div>
              <p className="section-label mb-4">Sobre Nós</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
                Assessoria aduaneira completa no coração do comércio exterior brasileiro
              </h2>
            </div>
          </FadeIn>
          <FadeIn delay={150}>
            <div className="space-y-5 text-muted-foreground leading-relaxed">
              <p>
                A <span className="text-foreground font-medium">Viamundi Gestão Aduaneira</span> é uma assessoria aduaneira completa, localizada estrategicamente em Vitória/ES — um dos principais polos portuários do Brasil e porta de entrada para operações de importação e exportação.
              </p>
              <p>
                Nossa equipe é formada por especialistas em despacho aduaneiro, classificação fiscal, regimes especiais e logística internacional, prontos para oferecer soluções personalizadas que reduzem custos e eliminam riscos operacionais.
              </p>
              <p>
                Atuamos de forma consultiva, acompanhando cada etapa da sua operação — da análise tributária à liberação da mercadoria — para que você possa focar no que realmente importa: o crescimento do seu negócio.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* MISSAO, VISAO E VALORES */}
      <section id="missao" data-testid="mission-section" className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <p className="section-label mb-4">Nossos Pilares</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-12 max-w-lg">
              Missão, Visão e Valores
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-6">
            <FadeIn delay={0}>
              <div data-testid="mission-card" className="rounded-2xl card-glow bg-card p-8 md:p-10 h-full flex flex-col">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                  <svg className="w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
                  </svg>
                </div>
                <h3 className="font-heading text-xl font-bold tracking-tight mb-3">Missão</h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                  Facilitar e otimizar as operações de comércio exterior dos nossos clientes, oferecendo assessoria aduaneira completa com excelência, transparência e comprometimento, contribuindo para o crescimento sustentável de cada empresa que atendemos.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={100}>
              <div data-testid="vision-card" className="rounded-2xl card-glow bg-card p-8 md:p-10 h-full flex flex-col">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                  <svg className="w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
                  </svg>
                </div>
                <h3 className="font-heading text-xl font-bold tracking-tight mb-3">Visão</h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                  Ser referência em assessoria aduaneira no Espírito Santo e no Brasil, reconhecida pela qualidade dos serviços, pela inovação nos processos e pela confiança construída com cada cliente.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={200}>
              <div data-testid="values-card" className="rounded-2xl card-glow bg-card p-8 md:p-10 h-full flex flex-col">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                  <svg className="w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
                  </svg>
                </div>
                <h3 className="font-heading text-xl font-bold tracking-tight mb-3">Valores</h3>
                <div className="text-sm text-muted-foreground leading-relaxed flex-1 space-y-2">
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" /> <span>Transparência em todas as relações</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" /> <span>Comprometimento com resultados</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" /> <span>Excelência operacional</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" /> <span>Inovação e tecnologia</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" /> <span>Ética profissional</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" /> <span>Proximidade com o cliente</span></div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="servicos" data-testid="services-section" className="py-20 md:py-32 px-6 md:px-12 max-w-7xl mx-auto">
        <FadeIn>
          <p className="section-label mb-4">Nossos Serviços</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-12 max-w-lg">
            Soluções completas para sua operação
          </h2>
        </FadeIn>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {services.map((s, i) => (
            <FadeIn key={s.title} delay={i * 100} className={`${s.span}`}>
              <div
                data-testid={`service-card-${i}`}
                className="service-card group relative overflow-hidden rounded-2xl card-glow bg-card h-full"
              >
                <img
                  src={s.img}
                  alt={s.title}
                  className="service-card-img absolute inset-0 w-full h-full object-cover opacity-15"
                  loading="lazy"
                />
                <div className="relative p-8 md:p-10 min-h-[260px] flex flex-col justify-end">
                  <s.icon className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-heading text-xl md:text-2xl font-bold tracking-tight">{s.title}</h3>
                  <p className="text-muted-foreground mt-2 text-sm md:text-base max-w-md leading-relaxed">{s.desc}</p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* DIFFERENTIALS */}
      <section id="diferenciais" data-testid="differentials-section" className="py-20 md:py-32 px-6 md:px-12 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <p className="section-label mb-4">Por que a Viamundi</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-12 max-w-lg">
              Por que empresas escolhem a Viamundi
            </h2>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {differentials.map((d, i) => (
              <FadeIn key={i} delay={i * 80}>
                <div
                  data-testid={`differential-${i}`}
                  className="flex items-start gap-4 p-6 rounded-2xl bg-card/50 border border-border/30 hover:border-primary/20 transition-colors"
                >
                  <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-sm md:text-base text-card-foreground leading-relaxed">{d}</span>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG */}
      <section id="blog" data-testid="blog-section" className="py-20 md:py-32 px-6 md:px-12 max-w-7xl mx-auto">
        <FadeIn>
          <p className="section-label mb-4">Conteúdo</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-12 max-w-lg">
            Conteúdo sobre Comércio Exterior
          </h2>
        </FadeIn>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post, i) => (
            <FadeIn key={post.id} delay={i * 100}>
              <Link to={`/blog/${post.id}`} data-testid={`blog-link-${i}`} className="block h-full">
                <article
                  data-testid={`blog-card-${i}`}
                  className="rounded-2xl card-glow bg-card overflow-hidden group h-full flex flex-col cursor-pointer"
                >
                  <div className="p-8 flex flex-col flex-1">
                    <div className="text-xs text-muted-foreground mb-3 uppercase tracking-wider">
                      {new Date(post.created_at).toLocaleDateString("pt-BR")}
                    </div>
                    <h3 className="font-heading text-lg font-bold tracking-tight group-hover:text-primary transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed flex-1">
                      {post.excerpt}
                    </p>
                    <div className="mt-6">
                      <span className="text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-2 group-hover:gap-3 transition-all">
                        Ler mais <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section id="contato" data-testid="contact-section" className="py-20 md:py-32 px-6 md:px-12 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-start">
            <FadeIn>
              <div>
                <p className="section-label mb-4">Contato</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
                  Fale com um especialista agora
                </h2>
                <p className="text-muted-foreground mt-4 leading-relaxed max-w-md">
                  Receba uma análise gratuita da sua operação e descubra como reduzir custos e eliminar riscos no seu comércio exterior.
                </p>
                <div className="mt-8 space-y-4">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Phone className="h-4 w-4 text-primary" />
                    <span>+55 27 98833-3380</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Mail className="h-4 w-4 text-primary" />
                    <span>contato@viamundiaduaneira.com.br</span>
                  </div>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={150}>
              <form
                data-testid="contact-form"
                onSubmit={handleSubmit}
                className="space-y-5 bg-card border border-border/50 rounded-2xl p-8"
              >
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nome</Label>
                  <Input
                    data-testid="contact-nome"
                    value={formData.nome}
                    onChange={(e) => setFormData(f => ({ ...f, nome: e.target.value }))}
                    required
                    className="mt-2 bg-background/50 border-border/50 rounded-none px-4 py-5 focus:border-primary"
                    placeholder="Seu nome completo"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                  <Input
                    data-testid="contact-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                    required
                    className="mt-2 bg-background/50 border-border/50 rounded-none px-4 py-5 focus:border-primary"
                    placeholder="seu@email.com"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Telefone</Label>
                  <Input
                    data-testid="contact-telefone"
                    value={formData.telefone}
                    onChange={(e) => setFormData(f => ({ ...f, telefone: e.target.value }))}
                    className="mt-2 bg-background/50 border-border/50 rounded-none px-4 py-5 focus:border-primary"
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Mensagem</Label>
                  <Textarea
                    data-testid="contact-mensagem"
                    value={formData.mensagem}
                    onChange={(e) => setFormData(f => ({ ...f, mensagem: e.target.value }))}
                    required
                    rows={4}
                    className="mt-2 bg-background/50 border-border/50 rounded-none px-4 py-5 focus:border-primary resize-none"
                    placeholder="Explique sua necessidade"
                  />
                </div>
                <Button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-none py-6 font-bold uppercase tracking-widest text-sm bg-primary text-primary-foreground glow-cyan hover:scale-[1.02] transition-transform"
                >
                  {submitting ? "Enviando..." : "Receber contato"}
                </Button>
              </form>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer data-testid="footer" className="border-t border-border/30 py-8 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Viamundi" className="h-10 w-auto object-contain" />
            <p className="text-sm text-muted-foreground">
              2026 Viamundi Aduaneira - viamundiaduaneira.com.br
            </p>
          </div>
          <a
            href="/admin"
            data-testid="admin-link"
            className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors"
          >
            Admin
          </a>
        </div>
      </footer>

      {/* WHATSAPP FAB */}
      <a
        href="https://wa.me/5527988333380"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-fab"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center whatsapp-glow hover:scale-110 transition-transform"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
}
